import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  FormGroup,
  Label,
  Input,
  Table,
  Spinner,
  Col,
  Button,
  Row,
} from "reactstrap";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import axios from "axios";

export const statusOrder = [
  "Prospecting",
  "Identified",
  "Validated",
  "Submitted",
  "Interviewing",
  "Client Review",
  "Verbal Won - Contracting",
];

const additionalStatusCategories = ["Placed", "Lost"];

// "Pipeline" pseudo-status means "all active pipeline statuses" -- excludes
// Placed/Lost/etc. that the backend pulls in for current-year tracking.
const isActivePipelineStatus = (status: string) => statusOrder.includes(status);

// Persists a piece of state in localStorage so it survives page refreshes.
// (Same pattern used in RecruiterPerformanceReport. Worth extracting to a
// shared util the next time we need it for a third component.)
function useStickyState<T>(
  key: string,
  defaultValue: T,
): [T, React.Dispatch<React.SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => {
    try {
      const stored = window.localStorage.getItem(key);
      if (stored !== null) return JSON.parse(stored) as T;
    } catch {
      /* ignore */
    }
    return defaultValue;
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* ignore */
    }
  }, [key, value]);

  return [value, setValue];
}

// Quick + dirty: keep conversion table code, just hide it in case they change their mind.
const SHOW_CONVERSION_TABLE = false;

// % weights by phase
const phaseWeights: Record<string, number> = {
  Prospecting: 0.1,
  Identified: 0.2,
  Validated: 0.25,
  Submitted: 0.5,
  Interviewing: 0.75,
  "Client Review": 0.75,
  "Verbal Won - Contracting": 0.9,
};

const getPhaseWeight = (status: string): number | null => {
  if (phaseWeights[status] != null) return phaseWeights[status];
  if (status.startsWith("Verbal Won")) return 0.9; // safety
  return null; // Placed/Lost or anything else
};

const formatWeightPct = (status: string): string => {
  const w = getPhaseWeight(status);
  if (w == null) return "N/A";
  return `${Math.round(w * 100)}%`;
};

const calcWeighted = (status: string, value: number): number | null => {
  const w = getPhaseWeight(status);
  if (w == null) return null;
  return value * w;
};

interface PipeLineConversionData {
  owner_id: number;
  status: string;
  job_count: number;
  projected_revenue: number;
  avg_time_diff: number;
  delta_count?: number;
  delta_value?: number;
}

interface PipeLineReportData {
  id: number;
  title: string;
  status: string;
  projected_revenue: number;
  dateAdded: string;
  ownerId: number;
  name: string; // full name
  companyName: string;
  notes: string;
  notesDate: string; // date of the last note update
}

interface ConversionInfo {
  jobCount: number;
  revenue: number;
  conversion: string;
  avgTimeDiff: number;
}

interface PipelineReportSummary {
  status: string;
  jobCount: number;
  totalRevenue: number;
}

const PipeLineReport = () => {
  console.log("Opening Pipeline Report");
  const abortCtrlRef = useRef<AbortController | null>(null);

  const [data, setData] = useState<PipeLineReportData[]>([]);
  const [filteredData, setFilteredData] = useState<PipeLineReportData[]>([]);
  const [statusJobs, setStatusJobs] = useState<PipeLineReportData[]>([]);

  const [conversionData, setConversionData] = useState<
    PipeLineConversionData[]
  >([]);
  const [conversionRates, setConversionRates] = useState<
    Record<string, ConversionInfo>
  >({});

  // Raw week/month datasets so the top pipeline tables can recalc by selected owner.
  const [oneWeekData, setOneWeekData] = useState<PipeLineReportData[]>([]);
  const [oneMonthData, setOneMonthData] = useState<PipeLineReportData[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [chartData, setChartData] = useState<ApexOptions | null>(null);
  const [totals, setTotals] = useState<Record<string, number>>({});
  const [totalPipelineValue, setTotalPipelineValue] = useState(0);

  // Per-row expand state for the "Last Update Note" column. Notes longer than
  // NOTE_PREVIEW_CHARS collapse by default with a "see more" toggle.
  const NOTE_PREVIEW_CHARS = 200;
  const [expandedNoteRows, setExpandedNoteRows] = useState<Set<number>>(
    new Set(),
  );
  const toggleNoteExpanded = (jobId: number) => {
    setExpandedNoteRows((prev) => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId);
      else next.add(jobId);
      return next;
    });
  };
  const [totalWeightedPipelineValue, setTotalWeightedPipelineValue] =
    useState(0);

  const [owners, setOwners] = useState<{ id: number | "All"; name: string }[]>(
    [],
  );
  const [selectedOwner, setSelectedOwner] = useState<number | "All">("All");

  // Default to "Pipeline" to display all jobs initially
  const [selectedStatus, setSelectedStatus] = useState<string | null>(
    "Pipeline",
  );

  // Per-user toggle: hide Placed/Lost rows from the default Pipeline view.
  // Stored in localStorage so each user keeps their own preference.
  const [hideClosed, setHideClosed] = useStickyState<boolean>(
    "pipelineReport.hideClosed",
    false,
  );

  const [serverStatus, setServerStatus] = useState<string>("unknown");

  // TODO: Remove hardcoding for Today on the Pipeline
  const today = new Date().toISOString().split("T")[0];

  // Monday of last week
  const oneWeek = new Date();
  const day = oneWeek.getDay(); // Sunday=0, Monday=1...
  // Go back to Monday of *last* week
  const diffToLastMonday = (day === 0 ? -6 : 1) - day - 7;
  oneWeek.setDate(oneWeek.getDate() + diffToLastMonday);
  const oneWeekStr = oneWeek.toISOString().split("T")[0];

  // First day of previous month
  const oneMonth = new Date();
  oneMonth.setMonth(oneMonth.getMonth() - 1, 1); // set to day 1
  const oneMonthStr = oneMonth.toISOString().split("T")[0];

  const [selectedDate, setSelectedDate] = useState<string>(today);

  // Sort pipeline data for consistent ordering in the funnel chart
  const sortPipelineData = (
    pipeline: PipeLineReportData[],
  ): PipeLineReportData[] => {
    return pipeline.sort(
      (a, b) => statusOrder.indexOf(a.status) - statusOrder.indexOf(b.status),
    );
  };

  // sortable config
  const [sortConfig, setSortConfig] = useState<{
    key: keyof PipeLineReportData | null;
    direction: "asc" | "desc";
  }>({ key: null, direction: "asc" });

  // Group totals by status (used for the funnel chart)
  const groupTotalsByStatus = (
    pipeline: PipeLineReportData[],
  ): Record<string, number> => {
    const totals: Record<string, number> = [
      ...statusOrder,
      ...additionalStatusCategories,
    ].reduce((acc, status) => ({ ...acc, [status]: 0 }), {});
    pipeline.forEach((item) => {
      if (totals.hasOwnProperty(item.status)) {
        totals[item.status] += item.projected_revenue;
      }
    });
    return totals;
  };

  // Update the funnel chart based on the filtered results
  // update the funnel chart based on the filtered results
  const updateChart = (filteredResults: PipeLineReportData[]) => {
    const newTotals = groupTotalsByStatus(filteredResults);
    setTotals(newTotals);

    const pipelineValue = statusOrder.reduce(
      (acc, status) => acc + (newTotals[status] || 0),
      0,
    );
    setTotalPipelineValue(pipelineValue);

    const weightedPipelineValue = statusOrder.reduce((acc, status) => {
      const weighted = calcWeighted(status, newTotals[status] || 0);
      return acc + (weighted || 0);
    }, 0);
    setTotalWeightedPipelineValue(weightedPipelineValue);

    setChartData({
      series: [
        {
          name: "Total Value",
          data: statusOrder.map((status) => newTotals[status]),
        },
      ],
      chart: {
        type: "bar",
        height: 500,
        toolbar: { show: true },
        events: {
          dataPointSelection: (_, __, config) => {
            const clickedStatus = statusOrder[config.dataPointIndex];
            setSelectedStatus(clickedStatus);
            setStatusJobs(
              filteredResults.filter((job) => job.status === clickedStatus),
            );
          },
        },
      },
      plotOptions: {
        bar: {
          borderRadius: 0,
          horizontal: true,
          distributed: true,
          barHeight: "80%",
          isFunnel: true,
        },
      },
      colors: [
        "#0094ba",
        "#2481aa",
        "#476d9b",
        "#6b5a8b",
        "#8f467b",
        "#b3336b",
        "#d61f5c",
        "#fa0c4c",
      ],
      dataLabels: {
        enabled: true,
        formatter: (val, opts) =>
          `${statusOrder[opts.dataPointIndex]} - $${val.toLocaleString()}`,
        dropShadow: { enabled: false },
        style: { fontSize: "14px", fontWeight: "400", colors: ["#000000"] },
      },
      tooltip: { enabled: false },
      title: { text: `Pipeline Report ${selectedDate}`, align: "center" },
      xaxis: { categories: statusOrder, labels: { show: false } },
      yaxis: {
        labels: {
          show: true,
          formatter: (_, index) => statusOrder[index],
          style: { fontSize: "14px", fontWeight: "bold", colors: ["#000000"] },
        },
      },
    });
  };

  // Compute conversion rates showing both counts and revenue (kept, hidden)
  const computeConversionRates = () => {
    const filteredConversions =
      selectedOwner === "All"
        ? conversionData
        : conversionData.filter((item) => item.owner_id === selectedOwner);

    const aggregated = filteredConversions.reduce(
      (acc, item) => {
        if (!acc[item.status]) {
          acc[item.status] = {
            jobCount: 0,
            revenue: 0,
            avgTimeDiff: 0,
            avgTimeDiffCount: 0,
          };
        }
        acc[item.status].jobCount += item.job_count;
        acc[item.status].revenue += item.projected_revenue;
        if (item.avg_time_diff > 0) {
          acc[item.status].avgTimeDiff += item.avg_time_diff;
          acc[item.status].avgTimeDiffCount += 1;
        }
        return acc;
      },
      {} as Record<
        string,
        {
          jobCount: number;
          revenue: number;
          avgTimeDiff: number;
          avgTimeDiffCount: number;
        }
      >,
    );

    const rates: Record<string, ConversionInfo> = {};

    statusOrder.forEach((status, index) => {
      const jobCount = aggregated[status]?.jobCount ?? 0;
      const revenue = aggregated[status]?.revenue ?? 0;

      let conversion = "";
      if (index === 0) {
        conversion = "";
      } else {
        const prevStatus = statusOrder[index - 1];
        const prevRevenue = aggregated[prevStatus]?.revenue ?? 0;
        conversion =
          prevRevenue > 0
            ? ((revenue / prevRevenue) * 100).toFixed(1) + "%"
            : "N/A";
      }

      rates[status] = {
        jobCount,
        revenue,
        conversion,
        avgTimeDiff:
          aggregated[status]?.avgTimeDiffCount > 0
            ? aggregated[status].avgTimeDiff /
              aggregated[status].avgTimeDiffCount
            : 0,
      };
    });

    const prospectingRevenue = aggregated["Prospecting"]?.revenue ?? 0;
    additionalStatusCategories.forEach((status) => {
      const jobCount = aggregated[status]?.jobCount ?? 0;
      const revenue = aggregated[status]?.revenue ?? 0;
      const conversion =
        prospectingRevenue > 0
          ? ((revenue / prospectingRevenue) * 100).toFixed(1) + "%"
          : "N/A";

      rates[status] = {
        jobCount,
        revenue,
        conversion,
        avgTimeDiff:
          aggregated[status]?.avgTimeDiffCount > 0
            ? aggregated[status].avgTimeDiff /
              aggregated[status].avgTimeDiffCount
            : 0,
      };
    });

    return rates;
  };

  const buildSummary = (
    rows: PipeLineReportData[],
    owner: number | "All",
  ): Record<string, PipelineReportSummary> => {
    const filtered =
      owner === "All" ? rows : rows.filter((r) => r.ownerId === owner);

    return filtered.reduce(
      (acc, item) => {
        if (!acc[item.status]) {
          acc[item.status] = {
            status: item.status,
            jobCount: 0,
            totalRevenue: 0,
          };
        }
        acc[item.status].jobCount += 1;
        acc[item.status].totalRevenue += item.projected_revenue || 0;
        return acc;
      },
      {} as Record<string, PipelineReportSummary>,
    );
  };

  const oneWeekSummary = useMemo(
    () => buildSummary(oneWeekData, selectedOwner),
    [oneWeekData, selectedOwner],
  );

  const oneMonthSummary = useMemo(
    () => buildSummary(oneMonthData, selectedOwner),
    [oneMonthData, selectedOwner],
  );

  // Fetch pipeline and conversion data
  const fetchData = async () => {
    setLoading(true);
    console.log("Fetching Data");
    const controller = new AbortController();
    abortCtrlRef.current = controller;

    try {
      const pipelineRes = await axios.get("/pipeline", {
        params: { asOfDate: selectedDate },
        signal: controller.signal,
        timeout: 5000,
      });

      const sorted = sortPipelineData(pipelineRes.data);

      // Keep conversion fetch even though table is hidden (quick + dirty)
      const convRes = await axios.get("/pipeline-conversion");

      const serverStatusRes = await axios.get("/status");
      const formatCentralTime = (utcString: string) => {
        if (!utcString) return "N/A";

        const date = new Date(utcString);

        const formatted = date.toLocaleString("en-US", {
          timeZone: "America/Chicago",
          weekday: "short",
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });

        const diffMinutes = Math.floor((Date.now() - date.getTime()) / 60000);

        if (diffMinutes < 1) return `${formatted} (just now) (CST)`;
        if (diffMinutes < 60)
          return `${formatted} (${diffMinutes} min ago) (CST)`;

        return `${formatted} (CST)`;
      };
      const raw = serverStatusRes.data.status.lastPipelineSyncDT;
      setServerStatus(formatCentralTime(raw));

      const conversionResults = convRes.data.map((item: any) => ({
        ...item,
        job_count: item.job_count != null ? parseInt(item.job_count, 10) : 0,
        projected_revenue: item.projected_revenue
          ? parseFloat(item.projected_revenue)
          : 0,
        avg_time_diff: item.avg_time_diff
          ? parseFloat(item.avg_time_diff)
          : null,
      }));
      setConversionData(conversionResults);

      const uniqueOwners = Array.from(
        new Map(
          sorted.map((item: PipeLineReportData) => [
            item.ownerId,
            {
              id: item.ownerId,
              name: item.name,
            },
          ]),
        ).values(),
      );
      setOwners([{ id: "All", name: "Sales Team" }, ...uniqueOwners]);

      setData(sorted);
      setFilteredData(sorted);
      updateChart(sorted);
      setStatusJobs(
        hideClosed
          ? sorted.filter((job) => isActivePipelineStatus(job.status))
          : sorted,
      );

      // Store raw week/month datasets so top tables can recalc by owner selection.
      const weekPipelineRes = await axios.get("/pipeline-delta", {
        params: { asOfDate: oneWeekStr },
        signal: controller.signal,
        timeout: 5000,
      });
      setOneWeekData(weekPipelineRes.data);

      const monthPipelineRes = await axios.get("/pipeline-delta", {
        params: { asOfDate: oneMonthStr },
        signal: controller.signal,
        timeout: 5000,
      });
      setOneMonthData(monthPipelineRes.data);

      setLoading(false);
    } catch (err: any) {
      console.error("Error fetching data:", err);
      setError(err.message);
      setLoading(false);
    }
  };

  const sortedJobs = useMemo(() => {
    if (!sortConfig.key) return statusJobs;
    const sorted = [...statusJobs].sort((a, b) => {
      let aVal: any = a[sortConfig.key!];
      let bVal: any = b[sortConfig.key!];

      if (sortConfig.key === "companyName") {
        aVal = a.companyName;
        bVal = b.companyName;
      }
      if (sortConfig.key === "name") {
        aVal = a.name;
        bVal = b.name;
      }
      if (sortConfig.key === "projected_revenue") {
        aVal = a.projected_revenue;
        bVal = b.projected_revenue;
      }
      if (sortConfig.key === "dateAdded") {
        aVal = new Date(a.dateAdded).getTime();
        bVal = new Date(b.dateAdded).getTime();
      }
      if (sortConfig.key === "notesDate") {
        aVal = a.notesDate ? new Date(a.notesDate).getTime() : 0;
        bVal = b.notesDate ? new Date(b.notesDate).getTime() : 0;
      }

      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [statusJobs, sortConfig]);

  const requestSort = (key: keyof PipeLineReportData) => {
    let direction: "asc" | "desc" = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const handleDownloadCSV = () => {
    if (!data || data.length === 0) return;

    const headers = [
      "Job ID",
      "Title",
      "Company",
      "Status",
      "Owner",
      "Value",
      "Weighted Value",
      "Date Added",
      "Last Update Note",
      "Last Update Note Date",
    ];

    const rows = data.map((job) => {
      const weighted = calcWeighted(job.status, job.projected_revenue);
      return [
        job.id || "",
        job.title || "",
        job.companyName || "",
        job.status || "",
        job.name,
        job.projected_revenue != null ? job.projected_revenue.toFixed(2) : "",
        weighted != null ? weighted.toFixed(2) : "",
        job.dateAdded ? new Date(job.dateAdded).toLocaleDateString() : "",
        job.notes || "No Update Note Available",
        job.notesDate ? new Date(job.notesDate).toLocaleDateString() : "",
      ];
    });

    const csvContent = [headers, ...rows]
      .map((row) =>
        row.map((field) => `"${String(field).replace(/"/g, '""')}"`).join(","),
      )
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "pipeline_report.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    // after setting statusJobs, reset sorting when status changes
    setSortConfig({ key: null, direction: "asc" });
  }, [statusJobs]);

  useEffect(() => {
    if (conversionData && conversionData.length > 0) {
      const newRates = computeConversionRates();
      setConversionRates(newRates);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversionData, selectedOwner]);

  useEffect(() => {
    fetchData();

    return () => {
      abortCtrlRef.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  // Main data filter by selected owner
  useEffect(() => {
    if (selectedOwner === "All") {
      setFilteredData(data);
      updateChart(data);
      if (selectedStatus === "Pipeline") {
        setStatusJobs(
          hideClosed
            ? data.filter((job) => isActivePipelineStatus(job.status))
            : data,
        );
      } else if (selectedStatus) {
        setStatusJobs(data.filter((job) => job.status === selectedStatus));
      }
    } else {
      const filteredResults = data.filter(
        (item) => item.ownerId === selectedOwner,
      );
      setFilteredData(filteredResults);
      updateChart(filteredResults);
      if (selectedStatus === "Pipeline") {
        setStatusJobs(
          hideClosed
            ? filteredResults.filter((job) =>
                isActivePipelineStatus(job.status),
              )
            : filteredResults,
        );
      } else if (selectedStatus) {
        setStatusJobs(
          filteredResults.filter((job) => job.status === selectedStatus),
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOwner, data, hideClosed]);

  const orderedStatuses = [...statusOrder, ...additionalStatusCategories];

  if (loading) {
    return (
      <>
        <div>Pulling fresh data from Bullhorn.</div>
        <div>Please wait 30 seconds… Make IT Happen today!</div>
        <Spinner color='primary' />
      </>
    );
  }
  if (error) {
    return <p>Error: {error}</p>;
  }

  return (
    <Card>
      {/* Conversion table kept but hidden */}
      {SHOW_CONVERSION_TABLE && (
        <CardHeader>
          <div style={{ marginTop: "10px" }}>
            <strong>Conversion Rates:</strong>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                marginTop: "5px",
                textAlign: "left",
                overflow: "wrap",
              }}
            >
              <thead>
                <tr>
                  <th style={{ border: "1px solid #ddd", padding: "4px" }}>
                    Phase
                  </th>
                  <th style={{ border: "1px solid #ddd", padding: "4px" }}>
                    Count
                  </th>
                  <th style={{ border: "1px solid #ddd", padding: "4px" }}>
                    $ Value
                  </th>
                  <th style={{ border: "1px solid #ddd", padding: "4px" }}>
                    Conversion
                  </th>
                </tr>
              </thead>
              <tbody>
                {orderedStatuses.map((status) => {
                  const phaseInfo = conversionRates[status];
                  return (
                    <tr
                      key={status}
                      style={
                        status === "Placed" || status === "Lost"
                          ? { fontWeight: "bold", backgroundColor: "#f5f5f5" }
                          : {}
                      }
                    >
                      <td style={{ border: "1px solid #ddd", padding: "4px" }}>
                        {status}
                      </td>
                      <td style={{ border: "1px solid #ddd", padding: "4px" }}>
                        {phaseInfo?.jobCount.toLocaleString() ?? 0}
                      </td>
                      <td style={{ border: "1px solid #ddd", padding: "4px" }}>
                        {phaseInfo?.revenue
                          ? `$${phaseInfo.revenue.toLocaleString()}`
                          : "$0"}
                      </td>
                      <td style={{ border: "1px solid #ddd", padding: "4px" }}>
                        {phaseInfo?.conversion || ""}
                        {phaseInfo &&
                        phaseInfo.avgTimeDiff > 0 &&
                        !isNaN(phaseInfo.avgTimeDiff) ? (
                          <span style={{ color: "gray", marginLeft: "5px" }}>
                            (Avg of {Math.round(phaseInfo.avgTimeDiff)} days
                            till final status)
                          </span>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardHeader>
      )}

      <CardHeader>
        <Row className='g-2 align-items-center mt-2'>
          <Col xs='6' md='auto'>
            <CardHeader>
              <div style={{ marginTop: "10px" }}>
                <strong>
                  Pipeline as of last month ({oneMonth.toLocaleDateString()})
                </strong>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    marginTop: "5px",
                    textAlign: "left",
                  }}
                >
                  <thead>
                    <tr>
                      <th style={{ border: "1px solid #ddd", padding: "4px" }}>
                        Phase
                      </th>
                      <th style={{ border: "1px solid #ddd", padding: "4px" }}>
                        %
                      </th>
                      <th style={{ border: "1px solid #ddd", padding: "4px" }}>
                        Count
                      </th>
                      <th style={{ border: "1px solid #ddd", padding: "4px" }}>
                        $ Value
                      </th>
                      <th style={{ border: "1px solid #ddd", padding: "4px" }}>
                        Weighted Pipeline
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {(() => {
                      let totalWeighted = 0;

                      const rows = orderedStatuses.map((status) => {
                        const phaseInfo = oneMonthSummary[status];
                        const value = phaseInfo?.totalRevenue ?? 0;
                        const weighted = calcWeighted(status, value);
                        if (weighted != null) totalWeighted += weighted;

                        return (
                          <tr
                            key={status}
                            style={
                              status === "Placed" || status === "Lost"
                                ? {
                                    fontWeight: "bold",
                                    backgroundColor: "#f5f5f5",
                                  }
                                : {}
                            }
                          >
                            <td
                              style={{
                                border: "1px solid #ddd",
                                padding: "4px",
                              }}
                            >
                              {status}
                            </td>
                            <td
                              style={{
                                border: "1px solid #ddd",
                                padding: "4px",
                              }}
                            >
                              {formatWeightPct(status)}
                            </td>
                            <td
                              style={{
                                border: "1px solid #ddd",
                                padding: "4px",
                              }}
                            >
                              {phaseInfo?.jobCount.toLocaleString() ?? 0}
                            </td>
                            <td
                              style={{
                                border: "1px solid #ddd",
                                padding: "4px",
                              }}
                            >
                              {phaseInfo?.totalRevenue
                                ? `$${phaseInfo.totalRevenue.toLocaleString()}`
                                : "$0"}
                            </td>
                            <td
                              style={{
                                border: "1px solid #ddd",
                                padding: "4px",
                              }}
                            >
                              {weighted == null
                                ? "N/A"
                                : `$${Math.round(weighted).toLocaleString()}`}
                            </td>
                          </tr>
                        );
                      });

                      rows.push(
                        <tr
                          key='total-weighted-month'
                          style={{ fontWeight: "bold" }}
                        >
                          <td
                            style={{
                              border: "1px solid #ddd",
                              padding: "4px",
                            }}
                          >
                            Total Weighted Pipeline
                          </td>
                          <td
                            style={{
                              border: "1px solid #ddd",
                              padding: "4px",
                            }}
                          ></td>
                          <td
                            style={{
                              border: "1px solid #ddd",
                              padding: "4px",
                            }}
                          ></td>
                          <td
                            style={{
                              border: "1px solid #ddd",
                              padding: "4px",
                            }}
                          >
                            N/A
                          </td>
                          <td
                            style={{
                              border: "1px solid #ddd",
                              padding: "4px",
                            }}
                          >
                            {`$${Math.round(totalWeighted).toLocaleString()}`}
                          </td>
                        </tr>,
                      );

                      return rows;
                    })()}
                  </tbody>
                </table>
              </div>
            </CardHeader>
          </Col>

          <Col xs='6' md='auto'>
            <CardHeader>
              <div style={{ marginTop: "10px" }}>
                <strong>
                  Pipeline as of last Monday ({oneWeek.toLocaleDateString()})
                </strong>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    marginTop: "5px",
                    textAlign: "left",
                  }}
                >
                  <thead>
                    <tr>
                      <th style={{ border: "1px solid #ddd", padding: "4px" }}>
                        Phase
                      </th>
                      <th style={{ border: "1px solid #ddd", padding: "4px" }}>
                        %
                      </th>
                      <th style={{ border: "1px solid #ddd", padding: "4px" }}>
                        Count
                      </th>
                      <th style={{ border: "1px solid #ddd", padding: "4px" }}>
                        $ Value
                      </th>
                      <th style={{ border: "1px solid #ddd", padding: "4px" }}>
                        Weighted Pipeline
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {(() => {
                      let totalWeighted = 0;

                      const rows = orderedStatuses.map((status) => {
                        const phaseInfo = oneWeekSummary[status];
                        const value = phaseInfo?.totalRevenue ?? 0;
                        const weighted = calcWeighted(status, value);
                        if (weighted != null) totalWeighted += weighted;

                        return (
                          <tr
                            key={status}
                            style={
                              status === "Placed" || status === "Lost"
                                ? {
                                    fontWeight: "bold",
                                    backgroundColor: "#f5f5f5",
                                  }
                                : {}
                            }
                          >
                            <td
                              style={{
                                border: "1px solid #ddd",
                                padding: "4px",
                              }}
                            >
                              {status}
                            </td>
                            <td
                              style={{
                                border: "1px solid #ddd",
                                padding: "4px",
                              }}
                            >
                              {formatWeightPct(status)}
                            </td>
                            <td
                              style={{
                                border: "1px solid #ddd",
                                padding: "4px",
                              }}
                            >
                              {phaseInfo?.jobCount.toLocaleString() ?? 0}
                            </td>
                            <td
                              style={{
                                border: "1px solid #ddd",
                                padding: "4px",
                              }}
                            >
                              {phaseInfo?.totalRevenue
                                ? `$${phaseInfo.totalRevenue.toLocaleString()}`
                                : "$0"}
                            </td>
                            <td
                              style={{
                                border: "1px solid #ddd",
                                padding: "4px",
                              }}
                            >
                              {weighted == null
                                ? "N/A"
                                : `$${Math.round(weighted).toLocaleString()}`}
                            </td>
                          </tr>
                        );
                      });

                      rows.push(
                        <tr
                          key='total-weighted-week'
                          style={{ fontWeight: "bold" }}
                        >
                          <td
                            style={{
                              border: "1px solid #ddd",
                              padding: "4px",
                            }}
                          >
                            Total Weighted Pipeline
                          </td>
                          <td
                            style={{
                              border: "1px solid #ddd",
                              padding: "4px",
                            }}
                          ></td>
                          <td
                            style={{
                              border: "1px solid #ddd",
                              padding: "4px",
                            }}
                          ></td>
                          <td
                            style={{
                              border: "1px solid #ddd",
                              padding: "4px",
                            }}
                          >
                            N/A
                          </td>
                          <td
                            style={{
                              border: "1px solid #ddd",
                              padding: "4px",
                            }}
                          >
                            {`$${Math.round(totalWeighted).toLocaleString()}`}
                          </td>
                        </tr>,
                      );

                      return rows;
                    })()}
                  </tbody>
                </table>
              </div>
            </CardHeader>
          </Col>
        </Row>

        <Row className='g-2 align-items-center'>
          {/* Left: Download */}
          <Col xs='12' md='auto'>
            <Button color='primary' onClick={handleDownloadCSV}>
              Download CSV
            </Button>
          </Col>

          {/* Right group: Label + Date + Refresh */}
          <Col
            xs='12'
            md='auto'
            className='ms-md-auto d-flex flex-column flex-md-row align-items-center gap-2'
          >
            <Label for='datePicker' className='mb-0'>
              Select Date
            </Label>

            <Input
              type='date'
              id='datePicker'
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              style={{ maxWidth: 200 }}
            />

            <Button
              color='primary'
              onClick={fetchData}
              disabled={!selectedDate}
            >
              Refresh
            </Button>
          </Col>
        </Row>
      </CardHeader>

      <CardBody>
        <div>
          Total Pipeline Value: $
          {Math.round(totalPipelineValue).toLocaleString()}
        </div>
        <div>
          Total Weighted Value: $
          {Math.round(totalWeightedPipelineValue).toLocaleString()}
        </div>

        <FormGroup>
          <Label for='ownerSelect'>Filter by Owner:</Label>
          <Input
            type='select'
            id='ownerSelect'
            value={selectedOwner}
            onChange={(e) =>
              setSelectedOwner(
                e.target.value === "All" ? "All" : parseInt(e.target.value, 10),
              )
            }
          >
            {owners.map((owner) => (
              <option key={owner.id} value={owner.id}>
                {owner.name}
              </option>
            ))}
          </Input>
        </FormGroup>

        <FormGroup check style={{ marginBottom: "1rem" }}>
          <Label check>
            <Input
              type='checkbox'
              checked={hideClosed}
              onChange={(e) => setHideClosed(e.target.checked)}
            />{" "}
            Hide closed jobs (Placed / Lost) from the Pipeline list
          </Label>
        </FormGroup>

        <ReactApexChart
          options={chartData!}
          series={chartData!.series as any}
          type='bar'
          height={500}
        />

        {selectedStatus && (
          <>
            <h5 className='mt-4'>
              {selectedStatus === "Pipeline"
                ? "All Pipeline Jobs"
                : `Jobs in "${selectedStatus}"`}
            </h5>
            <p>Last Pipeline Sync: {serverStatus}</p>

            <Table
              bordered
              striped
              style={{
                width: "100%",
                borderCollapse: "collapse",
                marginTop: "5px",
                textAlign: "left",
              }}
            >
              <thead>
                <tr>
                  {[
                    "id",
                    "title",
                    "clientCorporation",
                    "status",
                    "owner",
                    "projected_revenue",
                    "weighted_revenue",
                    "dateAdded",
                  ].map((col) => (
                    <th
                      key={col}
                      onClick={() => {
                        if (
                          [
                            "clientCorporation",
                            "status",
                            "owner",
                            "projected_revenue",
                            "dateAdded",
                          ].includes(col)
                        ) {
                          requestSort(col as keyof PipeLineReportData);
                        }
                      }}
                      style={{
                        cursor: [
                          "clientCorporation",
                          "status",
                          "owner",
                          "projected_revenue",
                          "dateAdded",
                        ].includes(col)
                          ? "pointer"
                          : "default",
                        ...(col === "title"
                          ? { width: 160, maxWidth: 160 }
                          : {}),
                      }}
                    >
                      {col === "clientCorporation"
                        ? "Client"
                        : col === "projected_revenue"
                          ? "Value"
                          : col === "weighted_revenue"
                            ? "Weighted Value"
                            : col === "dateAdded"
                              ? "Date Added"
                              : col.charAt(0).toUpperCase() + col.slice(1)}
                    </th>
                  ))}
                  <th style={{ minWidth: 400 }}>Last Update Note</th>
                  <th
                    onClick={() => requestSort("notesDate")}
                    style={{ cursor: "pointer" }}
                  >
                    Note Last Updated
                  </th>
                </tr>
              </thead>

              <tbody>
                {sortedJobs.length > 0 ? (
                  sortedJobs.map((job) => {
                    const weighted = calcWeighted(
                      job.status,
                      job.projected_revenue,
                    );
                    return (
                      <tr key={job.id}>
                        <td>
                          <a
                            href={`https://cls44.bullhornstaffing.com/BullhornSTAFFING/OpenWindow.cfm?Entity=JobOrder&id=${job.id}`}
                            target='_blank'
                            rel='noreferrer'
                          >
                            {job.id}
                          </a>
                        </td>
                        <td style={{ maxWidth: 160, wordBreak: "break-word" }}>
                          {job.title}
                        </td>
                        <td>{job.companyName}</td>
                        <td>{job.status}</td>
                        <td>{job.name}</td>
                        <td>${job.projected_revenue.toLocaleString()}</td>
                        <td>
                          {weighted == null
                            ? "N/A"
                            : `$${Math.round(weighted).toLocaleString()}`}
                        </td>
                        <td>{new Date(job.dateAdded).toLocaleDateString()}</td>
                        <td style={{ maxWidth: 400, verticalAlign: "top" }}>
                          {(() => {
                            const note =
                              job.notes || "No Update Note Available";
                            const isLong = note.length > NOTE_PREVIEW_CHARS;
                            const isExpanded = expandedNoteRows.has(job.id);
                            if (!isLong) {
                              return (
                                <span style={{ whiteSpace: "pre-wrap" }}>
                                  {note}
                                </span>
                              );
                            }
                            return (
                              <>
                                <span style={{ whiteSpace: "pre-wrap" }}>
                                  {isExpanded
                                    ? note
                                    : note
                                        .slice(0, NOTE_PREVIEW_CHARS)
                                        .trimEnd() + "…"}
                                </span>{" "}
                                <Button
                                  color='link'
                                  size='sm'
                                  style={{
                                    padding: 0,
                                    verticalAlign: "baseline",
                                  }}
                                  onClick={() => toggleNoteExpanded(job.id)}
                                  aria-expanded={isExpanded}
                                  aria-label={
                                    isExpanded ? "Collapse note" : "Expand note"
                                  }
                                >
                                  {isExpanded ? "see less" : "see more"}
                                </Button>
                              </>
                            );
                          })()}
                        </td>
                        <td>
                          {job.notesDate
                            ? new Date(job.notesDate).toLocaleDateString()
                            : ""}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={10} className='text-center'>
                      No jobs found
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </>
        )}
      </CardBody>
    </Card>
  );
};

export default PipeLineReport;
